import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Search } from "lucide-react";
import { api, BACKEND_URL, getAdminToken } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import StatusBadge from "@/components/StatusBadge";

const STATUSES = [
  ["", "All statuses"],
  ["started", "Started"],
  ["incomplete", "Incomplete"],
  ["submitted", "Submitted"],
  ["under_objective_review", "Under objective review"],
  ["waiting_for_documents", "Waiting for documents"],
  ["waiting_for_reference_responses", "Waiting for reference responses"],
  ["qualified_for_interview", "Qualified for interview"],
  ["interview_scheduled", "Interview scheduled"],
  ["selected", "Selected"],
  ["waitlisted", "Waitlisted"],
  ["not_selected", "Not selected"],
  ["withdrawn", "Withdrawn"],
  ["closed", "Closed"],
];

export default function AdminDashboard() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/applications", { params: { status: status || undefined, q: q || undefined } });
      setRows(data.applications);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const exportCsv = async () => {
    const t = getAdminToken();
    const res = await fetch(`${BACKEND_URL}/api/admin/applications.csv`, { headers: { Authorization: `Bearer ${t}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "applications.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-serif text-3xl" style={{ color: "#0B2D59" }}>Applications</h1>
          <p className="text-sm" style={{ color: "#475569" }}>Apply the same written criteria to every applicant.</p>
        </div>
        <button className="p2p-btn-outline inline-flex items-center gap-2" data-testid={ADMIN.exportCsv} onClick={exportCsv}>
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="p2p-card p-4 mb-5 flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#475569" }} />
          <input className="p2p-input pl-10" placeholder="Search by name, email, confirmation #"
            data-testid={ADMIN.searchInput}
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <select className="p2p-input" style={{ width: "auto" }} data-testid={ADMIN.statusFilter}
          value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button className="p2p-btn-secondary" onClick={load}>Apply</button>
      </div>

      <div className="p2p-card overflow-hidden">
        <table className="w-full text-sm" data-testid={ADMIN.appsTable}>
          <thead>
            <tr style={{ background: "#F4F8FB", color: "#475569" }}>
              <th className="text-left p-3">Applicant</th>
              <th className="text-left p-3 hidden md:table-cell">Submitted</th>
              <th className="text-left p-3 hidden md:table-cell">Move-in</th>
              <th className="text-left p-3 hidden lg:table-cell">Income</th>
              <th className="text-left p-3 hidden lg:table-cell">Refs / Docs</th>
              <th className="text-left p-3 hidden lg:table-cell">Review</th>
              <th className="text-left p-3">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="p-6 text-center" style={{ color: "#475569" }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="7" className="p-6 text-center" style={{ color: "#475569" }}>No applications yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={ADMIN.appRow(r.id)} className="border-t hover:bg-[#F4F8FB]" style={{ borderColor: "#E2EBF1" }}>
                <td className="p-3">
                  <div className="font-medium" style={{ color: "#0B2D59" }}>{r.name}</div>
                  <div className="text-xs" style={{ color: "#475569" }}>{r.confirmation_number} · {r.email}</div>
                </td>
                <td className="p-3 hidden md:table-cell" style={{ color: "#475569" }}>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}</td>
                <td className="p-3 hidden md:table-cell" style={{ color: "#475569" }}>{r.desired_move_in_date || "—"}</td>
                <td className="p-3 hidden lg:table-cell" style={{ color: "#475569" }}>{r.monthly_income ? `$${Number(r.monthly_income).toLocaleString()}/mo` : "—"}</td>
                <td className="p-3 hidden lg:table-cell" style={{ color: "#475569" }}>{r.references_count} refs · {r.documents_count} docs</td>
                <td className="p-3 hidden lg:table-cell text-xs" style={{ color: r.objective_review_status === "not_started" ? "#475569" : "#0B2D59" }}>
                  {(r.objective_review_status || "not_started").replaceAll("_", " ")}
                  {r.criteria_version ? ` · v${r.criteria_version}` : ""}
                </td>
                <td className="p-3"><StatusBadge status={r.status} /></td>
                <td className="p-3 text-right">
                  <Link to={`/admin/applications/${r.id}`} className="p2p-btn-outline text-sm" style={{ padding: "0.4rem 0.9rem" }} data-testid={`admin-app-open-${r.id}`}>Open</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
